import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { styled } from '@linaria/react';
import { AnimatePresence, motion } from 'framer-motion';
import { v4 as uuid } from 'uuid';
import { Portal } from '../components/designSystem/helpers/Portal';
import { Props as ToastProps, Toast } from '../components/designSystem/Toast';
type ToastType = {
  id: string;
  content: ReactNode;
  toastProps?: Omit<ToastProps, 'children'>;
};
export type ToastOpenType = (content: ReactNode, toastProps?: Omit<ToastProps, 'children'> & {
  singleToastMessage?: boolean;
}) => string;
export interface ToastContextValue {
  open: ToastOpenType;
  close: (id: string) => void;
  closeAll: () => void;
}
const _exp = /*#__PURE__*/() => motion.div;
const ToastWrapper = /*#__PURE__*/styled(_exp())({
  name: "ToastWrapper",
  class: "taialvt",
  propsAsIs: true
});
export const ToastContext = createContext<ToastContextValue>({
  open: () => '',
  close: () => null,
  closeAll: () => null
});
export const ToastProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [toasts, setToasts] = useState<ToastType[]>([]);
  const open: ToastOpenType = (content, toastProps): string => {
    const id = uuid();
    if (toastProps?.singleToastMessage) {
      setToasts([{
        id,
        content,
        toastProps
      }]);
    } else {
      setToasts(currentToasts => [...currentToasts, {
        id,
        content,
        toastProps
      }]);
    }
    return id;
  };
  const close: (id: string) => void = id => setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id));
  const closeAll: () => void = () => setToasts([]);
  const contextValue = useMemo(() => ({
    open,
    close,
    closeAll
  }), []);
  return <ToastContext.Provider value={contextValue}>
      {children}

      <Portal>
        <ToastWrapper layout>
          <AnimatePresence>
            {toasts.map(toast => {
            const {
              toastProps
            } = toast;
            return <Toast key={toast.id} {...toastProps} onClose={() => {
              close(toast.id);
              toastProps?.onClose && toastProps.onClose();
            }}>
                  {toast.content}
                </Toast>;
          })}
          </AnimatePresence>
        </ToastWrapper>
      </Portal>
    </ToastContext.Provider>;
};
export const useToastController = () => {
  const context = useContext(ToastContext);

  // Ensure context is not undefined
  if (!context) {
    throw new Error('useToastController must be used within a ToastProvider');
  }
  const {
    open,
    close,
    closeAll
  } = context;
  return {
    open,
    close: (toastId?: string) => {
      if (toastId) {
        close(toastId);
      } else {
        closeAll();
      }
    },
    closeAll
  };
};

require("./toastController.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./toastController.tsx");